import { isSSR } from '../../../helpers';
import trans from '../../../helpers/trans';
import { Customer } from '../../@account/Customer/Customer';
import {
    isBaseErrorResponse,
    isFetchResultClientErrorResponse,
    isFetchResultErrorResponse,
    isFetchResultSyliusErrorResponse,
    isNotFoundErrorResponse,
} from '../../Error/Error';
import {
    transformBaseErrorResponseToString,
    transformFetchResultClientErrorToString,
    transformFetchResultErrorToString,
    transformFetchResultSyliusErrorToString,
} from '../../Error/ErrorTransformers';
import {
    FetchResult,
    FetchResultClientError,
    FetchResultError,
    FetchResultSyliusError,
    FetchResultType,
} from '../../FetchResult/FetchResult';
import { BaseErrorResponse, Token, tokenKey } from './index';

interface SyliusCustomerRequestInit extends RequestInit {
    customer?: Customer;
}

export const syliusFetch = async <ResponseData>(
    endpoint: string,
    options?: RequestInit,
): Promise<FetchResult<ResponseData, string>> => {
    let data: ResponseData
    | BaseErrorResponse
    | FetchResultError
    | FetchResultClientError
    | FetchResultSyliusError
    | null;

    try {
        const apiUrl = isSSR
            ? process.env.REACT_APP_SYLIUS_URL || ''
            : process.env.REACT_APP_SYLIUS_PUBLIC_URL || '';

        const requestOptions: RequestInit = {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/ld+json',
                'Accept-Language': 'nl',
                ...options?.headers,
            },
        };

        const response = await fetch(apiUrl + endpoint, requestOptions);

        const statusString = response.status.toString();
        const isSuccessfulStatusCode = statusString.startsWith('2') || statusString.startsWith('3');
        const responseHasContent = ![202, 204].includes(response.status);

        data = responseHasContent ? await response.json() : null;

        if (!isSuccessfulStatusCode) {
            if (isBaseErrorResponse(data)) {
                return {
                    status: response.status,
                    type: FetchResultType.Error,
                    error: transformBaseErrorResponseToString(data),
                };
            }

            if (isFetchResultErrorResponse(data)) {
                return {
                    status: response.status,
                    type: FetchResultType.Error,
                    error: transformFetchResultErrorToString(data),
                };
            }

            if (isFetchResultClientErrorResponse(data)) {
                return {
                    status: response.status,
                    type: FetchResultType.Error,
                    error: transformFetchResultClientErrorToString(data),
                };
            }

            if (isNotFoundErrorResponse(data)) {
                return {
                    status: response.status,
                    type: FetchResultType.NotFoundError,
                    error: data,
                };
            }

            if (isFetchResultSyliusErrorResponse(data)) {
                return {
                    status: response.status,
                    type: FetchResultType.Error,
                    error: transformFetchResultSyliusErrorToString(data),
                };
            }

            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            };
        }

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: data as ResponseData,
        };
    } catch (error: unknown) {
        console.error(`[syliusFetch] -> ${endpoint}`, error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    } finally {
        data = null;
    }
};

export const authorizedSyliusFetch = async <ResponseData>(
    endpoint: string,
    options?: RequestInit,
): Promise<FetchResult<ResponseData, string>> => {
    try {
        const storedToken = localStorage.getItem(tokenKey) || sessionStorage.getItem(tokenKey);

        if (!storedToken) {
            // TODO: Log out
            throw new Error('No token found.');
        }

        const tokenData = JSON.parse(storedToken) as Token;

        return await syliusFetch<ResponseData>(endpoint, {
            ...options,
            headers: {
                ...options?.headers,
                Authorization: tokenData.token || '',
            },
        });
    } catch (error: unknown) {
        console.error('[authorizedSyliusFetch]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};

export const syliusCustomerFetch = async <ResponseData>(
    endpoint: string,
    options?: SyliusCustomerRequestInit,
): Promise<FetchResult<ResponseData, string>> => {
    const { customer, ...restOptions } = options || {};

    try {
        if (customer?.id) {
            return await authorizedSyliusFetch<ResponseData>(endpoint, { ...restOptions });
        }

        return await syliusFetch<ResponseData>(endpoint, { ...restOptions });
    } catch (error: unknown) {
        console.error('[syliusCustomerFetch]', error);

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        };
    }
};
